import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CircleAlert, Lock, Upload } from 'lucide-react';

import { describeError } from '../../api/client';
import {
  Button,
  Card,
  CardBody,
  CardHead,
  EmptyState,
  Pill,
} from '../../components/ui';
import {
  isCutRunActive,
  totalUsefulSeconds,
  useAnalyzeCut,
  useCurrentUser,
  useCutRun,
  useExtractCandidate,
  useUploadCutSource,
  validateCutSource,
} from '../../hooks';
import type { CutCandidate, CutRun, CutSettings, CutSource, Id } from '../../types/api';
import { CUT_SOURCE_ACCEPT, DEFAULT_CUT_SETTINGS } from '../../types/api';
import { CandidateCard } from './CandidateCard';
import { CandidatePreview } from './CandidatePreview';
import {
  canRelax,
  isDefaultSettings,
  megabytes,
  plural,
  relaxed,
  timecode,
} from './contract';
import styles from './CutPage.module.css';

export function CutPage() {
  const { can, isLoading } = useCurrentUser();
  const [params, setParams] = useSearchParams();
  const [settings, setSettings] = useState<CutSettings>(DEFAULT_CUT_SETTINGS);
  const [uploaded, setUploaded] = useState<CutSource | null>(null);
  const [problems, setProblems] = useState<string[]>([]);

  const runId = params.get('run') ?? undefined;
  const { upload, progress, reset: resetUpload } = useUploadCutSource();
  const analyze = useAnalyzeCut();
  const runQuery = useCutRun(runId);
  const run = runQuery.data;
  const source = uploaded ?? run?.source ?? null;

  const setRunId = useCallback(
    (next: Id | undefined) => {
      setParams(
        (prev) => {
          const search = new URLSearchParams(prev);
          if (next) search.set('run', next);
          else search.delete('run');
          return search;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const start = useCallback(
    async (sourceId: Id, next: CutSettings) => {
      const started = await analyze.mutateAsync({ source_id: sourceId, settings: next });
      setRunId(started.run_id);
    },
    [analyze, setRunId],
  );

  const handlePick = useCallback(
    async (file: File | null) => {
      const found = validateCutSource(file);
      setProblems(found);
      if (!file || found.length > 0) return;
      setRunId(undefined);
      const accepted = await upload.mutateAsync(file);
      setUploaded(accepted);
      await start(accepted.source_id, settings);
    },
    [settings, setRunId, start, upload],
  );

  const shownSettings = run?.settings;
  useEffect(() => {
    if (shownSettings) setSettings(shownSettings);
  }, [shownSettings]);

  const relax = useCallback(async () => {
    const softer = relaxed(settings);
    setSettings(softer);
    if (source) await start(source.source_id, softer);
  }, [settings, source, start]);

  const startOver = useCallback(() => {
    setUploaded(null);
    setRunId(undefined);
    setProblems([]);
    resetUpload();
  }, [resetUpload, setRunId]);

  if (isLoading) {
    return <p className="muted">Проверяем доступ…</p>;
  }

  if (!can('editor')) {
    return <AccessDenied />;
  }

  const busy = upload.isPending || analyze.isPending;
  const failure = upload.error ?? analyze.error ?? runQuery.error ?? null;

  return (
    <div className={styles.page}>
      <LaunchPanel
        settings={settings}
        onSettings={setSettings}
        onPick={handlePick}
        disabled={busy}
        uploadFraction={progress}
        problems={problems}
      />

      {failure && (
        <div className={styles.failure} role="alert">
          <CircleAlert size={14} strokeWidth={1.6} aria-hidden="true" />
          <span>{describeError(failure).message}</span>
          {describeError(failure).errorId && (
            <code className={styles.errorId}>{describeError(failure).errorId}</code>
          )}
        </div>
      )}

      {source && (
        <ResultBar
          source={source}
          justUploaded={Boolean(uploaded?.reused)}
          run={run}
          onReanalyze={() => void start(source.source_id, settings)}
          onStartOver={startOver}
          busy={busy || isCutRunActive(run)}
        />
      )}

      {run && isCutRunActive(run) && <RunProgress run={run} />}

      {run?.status === 'failed' && (
        <div className={styles.failure} role="alert">
          <CircleAlert size={14} strokeWidth={1.6} aria-hidden="true" />
          <span>Разбор не удался: {run.error ?? 'причина неизвестна'}</span>
        </div>
      )}

      {run?.status === 'succeeded' &&
        (run.candidates.length > 0 ? (
          <Candidates run={run} />
        ) : (
          <NothingFound run={run} onRelax={() => void relax()} onStartOver={startOver} />
        ))}

      {!source && !busy && <PipelinePanel />}
    </div>
  );
}

interface LaunchProps {
  settings: CutSettings;
  onSettings: (next: CutSettings) => void;
  onPick: (file: File | null) => void | Promise<void>;
  disabled: boolean;
  uploadFraction: number | null;
  problems: string[];
}

function LaunchPanel({
  settings,
  onSettings,
  onPick,
  disabled,
  uploadFraction,
  problems,
}: LaunchProps) {
  const [dragging, setDragging] = useState(false);
  const inputId = 'cut-source-input';

  return (
    <Card>
      <CardHead title="Исходник" hint="интервью или выступление целиком" />
      <CardBody pad className={styles.panelBody}>
        <label
          htmlFor={inputId}
          className={`${styles.dropzone} ${dragging ? styles.dropzoneActive : ''}`}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            if (!disabled) void onPick(event.dataTransfer.files?.[0] ?? null);
          }}
        >
          <input
            id={inputId}
            type="file"
            className={styles.hiddenInput}
            accept={CUT_SOURCE_ACCEPT}
            disabled={disabled}
            onChange={(event) => {
              const picked = event.target.files?.[0] ?? null;
              event.target.value = '';
              void onPick(picked);
            }}
          />
          <Upload size={22} strokeWidth={1.4} aria-hidden="true" />
          <div className={styles.dropTitle}>
            {uploadFraction !== null ? 'Файл уходит на сервер' : 'Перетащите файл или выберите'}
          </div>
          <div className={styles.dropHint}>
            Ищем куски, где человек смотрит в камеру и рот ничем не закрыт. Разбор часового
            исходника занимает 5–10 минут и идёт в фоне, так что вкладку можно закрыть.
          </div>
          <div className={styles.dropMeta}>mp4 · mov · mkv · webm · до 4 ГБ</div>
          {uploadFraction !== null && (
            <div className={styles.uploadBar}>
              <span className={styles.progressTrack} aria-hidden="true">
                <span
                  className={styles.progressFill}
                  style={{ width: `${Math.round(uploadFraction * 100)}%` }}
                />
              </span>
              <span className={styles.progressPct}>{Math.round(uploadFraction * 100)}%</span>
            </div>
          )}
        </label>

        {problems.length > 0 && (
          <ul className={styles.problems}>
            {problems.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        )}

        <div className={styles.settingsHead}>
          <span className={styles.rowLabel}>Что считать пригодным куском</span>
          <Button
            size="sm"
            onClick={() => onSettings(DEFAULT_CUT_SETTINGS)}
            disabled={disabled || isDefaultSettings(settings)}
          >
            Вернуть по умолчанию
          </Button>
        </div>

        <div className={styles.thresholds}>
          <Threshold
            k="Отворот головы, не больше"
            unit="°"
            value={settings.max_yaw_deg}
            min={5}
            max={45}
            step={1}
            disabled={disabled}
            onChange={(next) =>
              onSettings({
                ...settings,
                max_yaw_deg: next,
                ideal_yaw_deg: Math.min(settings.ideal_yaw_deg, next),
              })
            }
          />
          <Threshold
            k="Идеальный анфас, до"
            unit="°"
            value={settings.ideal_yaw_deg}
            min={1}
            max={settings.max_yaw_deg}
            step={1}
            disabled={disabled}
            onChange={(next) => onSettings({ ...settings, ideal_yaw_deg: next })}
          />
          <Threshold
            k="Наклон головы, не больше"
            unit="°"
            value={settings.max_pitch_deg}
            min={5}
            max={45}
            step={1}
            disabled={disabled}
            onChange={(next) =>
              onSettings({
                ...settings,
                max_pitch_deg: next,
                ideal_pitch_deg: Math.min(settings.ideal_pitch_deg, next),
              })
            }
          />
          <Threshold
            k="Идеальный наклон, до"
            unit="°"
            value={settings.ideal_pitch_deg}
            min={1}
            max={settings.max_pitch_deg}
            step={1}
            disabled={disabled}
            onChange={(next) => onSettings({ ...settings, ideal_pitch_deg: next })}
          />
          <Threshold
            k="Лицо в кадре, от"
            unit="% кадра"
            value={Math.round(settings.min_face_area_frac * 1000) / 10}
            min={0.5}
            max={10}
            step={0.5}
            disabled={disabled}
            onChange={(next) => onSettings({ ...settings, min_face_area_frac: next / 100 })}
          />
          <Threshold
            k="Длина куска, от"
            unit="с"
            value={settings.min_duration_s}
            min={1}
            max={60}
            step={1}
            disabled={disabled}
            onChange={(next) => onSettings({ ...settings, min_duration_s: next })}
          />
        </div>
      </CardBody>
    </Card>
  );
}

function Threshold({
  k,
  unit,
  value,
  min,
  max,
  step,
  disabled,
  onChange,
}: {
  k: string;
  unit: string;
  value: number;
  min: number;
  max: number;
  step: number;
  disabled: boolean;
  onChange: (next: number) => void;
}) {
  return (
    <label className={styles.threshold}>
      <span className={styles.thresholdKey}>{k}</span>
      <input
        className={styles.thresholdInput}
        type="number"
        inputMode="decimal"
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (Number.isFinite(next)) onChange(Math.min(max, Math.max(min, next)));
        }}
      />
      <span className={styles.thresholdUnit}>{unit}</span>
    </label>
  );
}

const PHASES: readonly { name: string; text: string }[] = [
  { name: 'Загрузка', text: 'файл уходит на сервер, видно сколько процентов передано' },
  {
    name: 'Разбор кадров',
    text: 'смотрим кадр за кадром: есть ли лицо, куда повёрнута голова, виден ли рот',
  },
  { name: 'Уточнение', text: 'пересматриваем найденные места внимательнее' },
  {
    name: 'Отбор фрагментов',
    text: 'собираем куски целиком и раскладываем по качеству, лучшие сверху',
  },
];

function PipelinePanel() {
  return (
    <Card>
      <CardHead title="Как пойдёт разбор" hint="вкладку можно закрыть, работа продолжится" />
      <CardBody pad className={styles.panelBody}>
        <div className={styles.phases}>
          {PHASES.map((phase, index) => (
            <div
              key={phase.name}
              className={styles.phaseBar}
              style={{ flex: index === 1 ? 5 : index === 2 ? 3 : 1 }}
              title={phase.name}
            />
          ))}
        </div>
        <div className={styles.phaseList}>
          {PHASES.map((phase) => (
            <div key={phase.name} className={styles.phase}>
              <span className={styles.phaseName}>{phase.name}</span>
              <span className={styles.phaseText}>{phase.text}</span>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

function RunProgress({ run }: { run: CutRun }) {
  const pct = Math.round(run.progress * 100);
  return (
    <Card>
      <CardHead title="Идёт разбор" hint={run.stage || 'ждём первых результатов'} />
      <CardBody pad className={styles.panelBody}>
        <div className={styles.runProgress}>
          <span className={styles.progressTrack} aria-hidden="true">
            <span className={styles.progressFill} style={{ width: `${pct}%` }} />
          </span>
          <span className={styles.progressPct}>{pct}%</span>
        </div>
        <p className={styles.panelHint}>
          Вкладку можно закрыть, работа продолжится. Часовой исходник занимает 5–10 минут.
        </p>
      </CardBody>
    </Card>
  );
}

interface ResultBarProps {
  source: CutSource;
  justUploaded: boolean;
  run: CutRun | undefined;
  onReanalyze: () => void;
  onStartOver: () => void;
  busy: boolean;
}

function ResultBar({
  source,
  justUploaded,
  run,
  onReanalyze,
  onStartOver,
  busy,
}: ResultBarProps) {
  const useful = totalUsefulSeconds(run?.candidates ?? []);
  const found = run?.status === 'succeeded';

  return (
    <div className={styles.resultBar}>
      {justUploaded && (
        <Pill tone="ok" className={styles.sampleChip}>
          файл уже был загружен
        </Pill>
      )}
      <span className={styles.fileName} title={source.filename}>
        {source.filename}
      </span>
      <span className={styles.fileMeta}>
        {timecode(source.duration_s)} · {source.width}×{source.height} ·{' '}
        {megabytes(source.size_bytes)}
      </span>
      <span className={styles.divider} aria-hidden="true" />
      {found && (
        <Pill variant="solid">
          {run.candidates.length > 0
            ? `${run.candidates.length} ${plural(run.candidates.length, [
                'фрагмент',
                'фрагмента',
                'фрагментов',
              ])}`
            : 'ничего не найдено'}
        </Pill>
      )}
      {found && run.candidates.length > 0 && (
        <span className={styles.panelHint}>{timecode(useful)} годного материала</span>
      )}
      <div className={styles.barRight}>
        <Button size="sm" disabled={busy} onClick={onReanalyze}>
          Разобрать заново
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onStartOver}>
          Другой файл
        </Button>
      </div>
    </div>
  );
}

function Candidates({ run }: { run: CutRun }) {
  const { extract, pendingIndex } = useExtractCandidate(run.run_id);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  const preview = run.candidates.find((item) => item.index === previewIndex) ?? null;
  const waitHint = (index: number) =>
    pendingIndex !== null && pendingIndex !== index ? 'Сначала дорежется предыдущий' : undefined;
  const cut = (item: CutCandidate) => extract.mutate({ index: item.index, with_audio: true });

  return (
    <>
      <div className={styles.grid}>
        {run.candidates.map((candidate) => (
          <CandidateCard
            key={candidate.index}
            candidate={candidate}
            busy={pendingIndex === candidate.index}
            actionsHint={waitHint(candidate.index)}
            onExtract={cut}
            onPlay={(item) => setPreviewIndex(item.index)}
          />
        ))}
      </div>

      <CandidatePreview
        candidate={preview}
        busy={preview !== null && pendingIndex === preview.index}
        hint={preview ? waitHint(preview.index) : undefined}
        onExtract={cut}
        onClose={() => setPreviewIndex(null)}
      />
    </>
  );
}

function NothingFound({
  run,
  onRelax,
  onStartOver,
}: {
  run: CutRun;
  onRelax: () => void;
  onStartOver: () => void;
}) {
  const explanation = run.empty_reason;

  return (
    <section className={styles.empty}>
      <EmptyState
        icon={<CircleAlert size={18} strokeWidth={1.4} className={styles.emptyIcon} />}
        title="Пригодных фрагментов не нашлось"
        description={
          <>
            <span className={styles.emptyLead}>
              Разобрали {timecode(run.source.duration_s)}. {explanation?.headline ?? ''}
            </span>
            {explanation?.advice && (
              <span className={styles.emptyAdvice}>{explanation.advice}</span>
            )}
          </>
        }
        action={
          <div className={styles.emptyActions}>
            <Button onClick={onRelax} disabled={!canRelax(run.settings)}>
              Смягчить отбор
            </Button>
            <Button variant="primary" onClick={onStartOver}>
              Другой файл
            </Button>
          </div>
        }
      />

      {explanation && explanation.reasons.length > 0 && (
        <div className={styles.reasons}>
          <span className={styles.reasonsTitle}>что ещё мешало</span>
          {explanation.reasons.map((item) => (
            <div key={item.reason} className={styles.reasonRow}>
              <span className={styles.reasonLabel} title={item.label}>
                {item.label}
              </span>
              <span className={styles.reasonTrack} aria-hidden="true">
                <span
                  className={styles.reasonFill}
                  style={{ width: `${Math.round(item.share * 100)}%` }}
                />
              </span>
              <span className={styles.reasonShare}>{Math.round(item.share * 100)}%</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function AccessDenied() {
  return (
    <section className={styles.denied}>
      <div className={styles.deniedHead}>
        <Lock size={17} strokeWidth={1.5} className={styles.deniedIcon} aria-hidden="true" />
        Недостаточно прав
      </div>
      <p className={styles.deniedText}>
        Нарезка доступна редакторам. Библиотеку смотреть и скачивать готовое можно.
      </p>
      <div className={styles.kv}>
        <div className={styles.kvKey}>Ваша роль</div>
        <div className={styles.kvValue}>наблюдатель</div>
        <div className={styles.kvKey}>Нужна роль</div>
        <div className={styles.kvValue}>редактор</div>
        <div className={styles.kvKey}>Кто выдаёт</div>
        <div className={styles.kvValue}>администратор «Монтажной»</div>
      </div>
    </section>
  );
}
